import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function ContactPage() {
  useEffect(() => {
    document.title = 'Contact Henry Adebisi';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Connect with Henry for strategic finance advisory, speaking engagements, or partnership opportunities focused on innovation and sustainable business growth.');
    }
    
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://henryadebisi.com'}/contact`);
    }
    
    const keywords = document.querySelector('meta[name="keywords"]');
    if (keywords) {
      keywords.setAttribute('content', 'contact Henry Adebisi, strategic finance advisory, speaking engagements, partnership opportunities');
    }

    // Add Schema.org JSON-LD
    const existingSchema = document.querySelector('script[type="application/ld+json"]');
    if (existingSchema) {
      existingSchema.remove();
    }

    const schema = {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Contact Henry Adebisi",
      "description": "Connect with Henry for strategic finance advisory, speaking engagements, or partnership opportunities focused on innovation and sustainable business growth.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://henryadebisi.com'}/contact`,
      "mainEntity": {
        "@type": "Person",
        "name": "Henry Olamide Adebisi",
        "email": "henry@afristakes.com",
        "sameAs": [
          "https://www.linkedin.com/in/henry-adebisi-311049b1/",
          "https://x.com/henryadebc?s=21"
        ]
      }
    };

    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.textContent = JSON.stringify(schema);
    document.head.appendChild(schemaScript);

    return () => {
      const schemaToRemove = document.querySelector('script[type="application/ld+json"]');
      if (schemaToRemove) {
        schemaToRemove.remove();
      }
    };
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formDataToSubmit = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSubmit.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d46b1mnjsic9gsa4u27g', {
        method: 'POST',
        body: new URLSearchParams(formDataToSubmit as any)
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', organization: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: 'ri-mail-line',
      title: 'Email',
      value: 'henry@afristakes.com',
      href: 'mailto:henry@afristakes.com'
    },
    {
      icon: 'ri-linkedin-line',
      title: 'LinkedIn',
      value: 'Connect on LinkedIn',
      href: 'https://www.linkedin.com/in/henry-adebisi-311049b1/'
    },
    {
      icon: 'ri-twitter-x-line',
      title: 'Twitter',
      value: 'Follow on X',
      href: 'https://x.com/henryadebc?s=21'
    }
  ];

  return (
    <div className="min-h-screen bg-off-white">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="font-sora font-bold text-4xl lg:text-5xl text-navy mb-6">
              Let's Connect
            </h1>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto leading-relaxed">
              Ready to discuss strategic finance, fintech innovation, or partnership opportunities? 
              Henry is available for advisory roles, speaking engagements, and strategic collaborations.
            </p>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              {/* Contact Info & Portrait */}
              <div>
                <div className="mb-12">
                  <img 
                    src="https://static.readdy.ai/image/6b84a11e410079b56c6291a27cbf72c2/d5c73e66af06aaef040309ea2cd56cf3.jpeg"
                    alt="Henry Olamide Adebisi"
                    className="rounded-lg shadow-xl object-cover object-top w-full max-w-md mx-auto"
                  />
                </div>
                
                <div className="space-y-6">
                  <h2 className="font-sora font-bold text-2xl text-navy mb-6">
                    Get in Touch
                  </h2>
                  
                  {contactInfo.map((info, index) => (
                    <a 
                      key={index}
                      href={info.href}
                      target={info.href.startsWith('http') ? '_blank' : undefined}
                      rel={info.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                      className="flex items-center p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200"
                    >
                      <div className="w-12 h-12 bg-gold/10 rounded-lg flex items-center justify-center mr-4">
                        <i className={`${info.icon} text-xl text-gold`}></i>
                      </div>
                      <div>
                        <h3 className="font-semibold text-navy">{info.title}</h3>
                        <p className="text-gray-600">{info.value}</p>
                      </div>
                    </a>
                  ))}
                </div>

                <div className="mt-12 p-6 bg-white rounded-lg shadow-sm">
                  <h3 className="font-sora font-semibold text-lg text-navy mb-4">
                    Available For:
                  </h3>
                  <ul className="space-y-2">
                    <li className="flex items-center text-gray-600">
                      <i className="ri-check-line text-gold mr-3"></i>
                      Strategic finance advisory
                    </li>
                    <li className="flex items-center text-gray-600">
                      <i className="ri-check-line text-gold mr-3"></i>
                      Speaking engagements
                    </li>
                    <li className="flex items-center text-gray-600">
                      <i className="ri-check-line text-gold mr-3"></i>
                      Board positions
                    </li>
                    <li className="flex items-center text-gray-600">
                      <i className="ri-check-line text-gold mr-3"></i>
                      Partnership opportunities
                    </li>
                    <li className="flex items-center text-gray-600">
                      <i className="ri-check-line text-gold mr-3"></i>
                      Media interviews
                    </li>
                  </ul>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-white rounded-lg shadow-lg p-8">
                <h2 className="font-sora font-bold text-2xl text-navy mb-6">
                  Send a Message
                </h2>
                
                {submitStatus === 'success' && (
                  <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-green-800">
                      <i className="ri-check-line mr-2"></i>
                      Thank you for your message! Henry will get back to you soon.
                    </p>
                  </div>
                )}
                
                {submitStatus === 'error' && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-800">
                      <i className="ri-error-warning-line mr-2"></i>
                      There was an error sending your message. Please try again.
                    </p>
                  </div>
                )}

                <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Name *
                      </label>
                      <input 
                        type="text" 
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
                        placeholder="Your full name"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email *
                      </label>
                      <input 
                        type="email" 
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Organization (Optional)
                    </label>
                    <input 
                      type="text" 
                      name="organization"
                      value={formData.organization}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
                      placeholder="Company or organization name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Message *
                    </label>
                    <textarea 
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent resize-none"
                      placeholder="Tell Henry about your project, opportunity, or how you'd like to collaborate..."
                      maxLength={500}
                    ></textarea>
                    <p className="text-gray-500 text-sm mt-1">Maximum 500 characters</p>
                  </div>
                  
                  <Button 
                    variant="secondary" 
                    size="lg" 
                    className="w-full"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line mr-2 animate-spin"></i>
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line mr-2"></i>
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Actions */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-sora font-bold text-3xl text-navy mb-8">
              Quick Actions
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Button variant="outline" size="lg" href="/speaking" className="h-auto py-6 flex-col">
                <i className="ri-mic-line text-2xl mb-2"></i>
                <span>Book Speaking</span>
              </Button>
              <Button variant="outline" size="lg" href="/ventures" className="h-auto py-6 flex-col">
                <i className="ri-handshake-line text-2xl mb-2"></i>
                <span>Explore Partnerships</span>
              </Button>
              <Button variant="outline" size="lg" href="/writing" className="h-auto py-6 flex-col">
                <i className="ri-article-line text-2xl mb-2"></i>
                <span>Read Articles</span>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
